import { ExternalLinkIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Collapse,
  Flex,
  FormControl,
  FormErrorMessage,
  Link,
  Radio,
  RadioGroup,
  Text,
  useBoolean,
  VStack
} from '@chakra-ui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { add, format, isAfter } from 'date-fns'
import { useEffect, useMemo } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { request_fixedAssets_depreciationMethod } from 'src/api/models/request_fixedAssets_depreciationMethod'
import { response_fixedAsset } from 'src/api/models/response_fixedAsset'
import { schemaForType } from 'src/common/schemeForType'
import { AlertMessage } from 'src/components/common/AlertMessage'
import { FormNumberInput } from 'src/components/common/Forms/FormNumberInput'
import { FormPriceAmountInput } from 'src/components/common/Forms/FormPriceAmountInput'
import { FormSelect } from 'src/components/common/Forms/FormSelect'
import { FormYearSelect } from 'src/components/common/Forms/FormYearSelect'
import { SubFormSection } from 'src/components/common/Forms/SubFormSection'
import { TriangleArrowRightIcon } from 'src/components/common/Icons/TriangleArrowRightIcon'
import { useConfirmDialog } from 'src/components/common/Modals/ConfirmDialog'
import { QuestionTooltip } from 'src/components/common/QuestionToolTip'
import { useFixedAsset } from 'src/hooks/useFixedAsset'
import { useProcessingYear } from 'src/hooks/useProcessingYear'
import { FormSection } from 'src/pages/tax_tasks/opening/components/TaxTaskTodoList/TaxTaskTodoModal/FormSection'
import { SaveButtons } from 'src/pages/tax_tasks/opening/components/TaxTaskTodoList/TaxTaskTodoModal/SaveButtons'
import { SubTodoProps } from 'src/pages/tax_tasks/opening/components/TaxTaskTodoList/TaxTaskTodoModal/TodoOne/SubTodoOne'
import * as z from 'zod'
import { DataDisplay } from './DataDisplay'
import { getDepreciationMethodName } from './SubTodoTwo'

const {
  STRAIGHT_LINE,
  DECLINING_BALANCE,
  PETTY_SUM,
  LUMP_SUM,
  STRAIGHT_LINE_METHOD,
  VOLUNTARY
} = response_fixedAsset.depreciation_method

const { CEIL, FLOOR, ROUND } =
  request_fixedAssets_depreciationMethod.amount_rounding

const getAmountRoundingMethodName = (
  method?: request_fixedAssets_depreciationMethod.amount_rounding | null
) => {
  switch (method) {
    case CEIL:
      return '切り上げ'
    case FLOOR:
      return '切り下げ'
    case ROUND:
      return '四捨五入'

    default:
      return '切り上げ'
  }
}

export const SubTodoThree: React.VFC<
  SubTodoProps & {
    index: number
    title: string
    id: string
    fixedAsset: response_fixedAsset | undefined
    isOnCreateModal: boolean
  }
> = ({
  title,
  id,
  index,
  done,
  isEditing,
  fixedAsset,
  isOnCreateModal,
  setIsEditing
}) => {
  const { processing_year } = useProcessingYear()
  const { patchFixedAssetsDepreciationMethod } = useFixedAsset(fixedAsset?.id)
  const [renderCompleteDialog, { onOpen: onOpenCompleteDialog }] =
    useConfirmDialog()

  // 取得日が現在操作している会計年度の場合、期首残高と改定取得価額のフォームはdisabled
  const isAcquisitionDateIsSameAsProcessingYear =
    new Date(fixedAsset?.acquisition_date || new Date()).getFullYear() ===
    processing_year?.processing_year

  // 編集中の固定資産が前年度以前に償却を持つか
  const hasPreviousDepreciation =
    fixedAsset && done && fixedAsset.has_pre_year_depreciation

  const schema = useMemo(
    () =>
      schemaForType<request_fixedAssets_depreciationMethod>()(
        z
          .object({
            depreciation_method: z
              .union([
                z.literal(DECLINING_BALANCE),
                z.literal(LUMP_SUM),
                z.literal(PETTY_SUM),
                z.literal(STRAIGHT_LINE),
                z.literal(STRAIGHT_LINE_METHOD),
                z.literal(VOLUNTARY)
              ])
              .refine((value) => {
                if (
                  value === PETTY_SUM &&
                  fixedAsset &&
                  (fixedAsset.acquisition_amount < 100000 ||
                    fixedAsset.acquisition_amount >= 300000)
                ) {
                  return false
                }
                return true
              }, '少額償却を選ぶ場合、金額は10万円以上30万円未満で入力してください。')
              .refine((value) => {
                if (
                  value === LUMP_SUM &&
                  fixedAsset &&
                  (fixedAsset.acquisition_amount < 100000 ||
                    fixedAsset.acquisition_amount >= 200000)
                ) {
                  return false
                }
                return true
              }, '一括償却を選ぶ場合、金額は10万円以上20万円未満で入力してください。'),
            durable_year: z
              .number({
                invalid_type_error: '耐用年数は２年以上で入力してください'
              })
              .max(100, '耐用年数は、100年以下で入力してください')
              .int('耐用年数は、整数値で入力してください')
              .optional(),
            service_date: z.preprocess(
              (value) => {
                if (value === '' || typeof value !== 'string') return undefined

                return new Date(value)
              },
              z
                .date({
                  required_error: '使用開始日を入力してください',
                  invalid_type_error: '正しい日付を入力してください'
                })
                .min(new Date('1900/1/1'), {
                  message:
                    '使用開始日は、1900/1/1 から 2099/12/31の間の日付を入力してください'
                })
                .max(new Date('2099/12/31'), {
                  message:
                    '使用開始日は、1900/1/1 から 2099/12/31の間の日付を入力してください'
                })
                .refine(
                  (value) =>
                    fixedAsset &&
                    fixedAsset.service_date &&
                    isAfter(
                      add(value, { days: 1 }),
                      new Date(fixedAsset.acquisition_date)
                    ),
                  `使用開始日は取得日${
                    fixedAsset?.service_date
                      ? `(${format(
                          new Date(fixedAsset.service_date),
                          'yyyy/MM/dd'
                        )})`
                      : ''
                  }以降の日付を入力してください。`
                )
                .transform((v) => {
                  return format(new Date(v), 'yyyy-MM-dd')
                })
            ),
            business_rate: z
              .number({
                required_error: '0~100%の間で入力してください',
                invalid_type_error: '0~100%の間で入力してください'
              })
              .min(0, '0~100%の間で入力してください')
              .max(100, '0~100%の間で入力してください')
              .refine((value) => {
                if (Number.isInteger(value)) return true
                if (Number.isNaN(Number(value))) return false
                const strArr = value.toString().split('.')
                const floatStr = strArr[1]
                return floatStr && floatStr.length < 11
              }, '事業利用割合は、小数点以下10桁までで入力してください'),
            amount_rounding: z.union([
              z.literal(CEIL),
              z.literal(FLOOR),
              z.literal(ROUND)
            ]),
            depreciation_amount: z
              .number()
              .min(0, '金額は、0円以上の金額を入力してください')
              .max(
                999999999,
                '金額は、999,999,999円以下の金額を入力してください'
              )
              .int('償却費は、整数値で入力してください')
              .optional(),
            special_depreciation_amount: z
              .number()
              .min(0, '金額は、0円以上の金額を入力してください')
              .max(
                999999999,
                '金額は、999,999,999円以下の金額を入力してください'
              )
              .int('特別償却費は、整数値で入力してください'),
            beginning_balance: z
              .number()
              .min(0, '金額は、0円以上の金額を入力してください')
              .max(
                999999999,
                '金額は、999,999,999円以下の金額を入力してください'
              )
              .int('期首残高は、整数値で入力してください')
              .refine((value) => {
                if (!fixedAsset) {
                  return true
                }
                return value <= fixedAsset.acquisition_amount
              }, `期首残高が取得価格(${fixedAsset?.acquisition_amount || 0})を超えています。期首残高を変更してください。`),
            revised_acquisition_amount: z
              .number()
              .min(0, '金額は、0円以上の金額を入力してください')
              .max(
                999999999,
                '金額は、999,999,999円以下の金額を入力してください'
              )
              .int('改定取得価額は、整数値で入力してください')
          })
          .transform((data) => {
            let returnValue = { ...data }
            if (fixedAsset?.account_item.name === '開業費') {
              if (data.depreciation_method === VOLUNTARY) {
                returnValue = { ...returnValue, durable_year: undefined }
              }
              if (data.depreciation_method === STRAIGHT_LINE_METHOD) {
                returnValue = { ...returnValue, durable_year: 5 }
              }
            }

            // 取得日が作業年度の場合、期首残高と改定取得価額は変更不可
            if (fixedAsset && isAcquisitionDateIsSameAsProcessingYear) {
              returnValue = {
                ...returnValue,
                beginning_balance:
                  fixedAsset.depreciation?.beginning_balance || 0,
                revised_acquisition_amount: 0
              }
            }

            // 耐用年数を2年以下で登録したら改定取得価格のフォームはdisableにする
            if (data.durable_year && data.durable_year <= 2) {
              returnValue = {
                ...returnValue,
                revised_acquisition_amount: 0
              }
            }

            // 任意償却のときのみ償却額を送る
            if (data.depreciation_method !== VOLUNTARY) {
              returnValue = {
                ...returnValue,
                depreciation_amount: undefined
              }
            }

            return returnValue
          })
          .superRefine((data, ctx) => {
            // 基本的に耐用年数は２年以上、 カテゴリが開業費かつ任意償却の時のみundefinedを許容
            if (
              !(
                fixedAsset?.account_item.name === '開業費' &&
                data.depreciation_method === VOLUNTARY
              ) &&
              (data.durable_year === undefined ||
                (data.depreciation_method !== PETTY_SUM &&
                  data.durable_year < 2))
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: '耐用年数は、2年以上で入力してください',
                path: ['durable_year']
              })
            }
            if (
              data.revised_acquisition_amount > 0 &&
              (data.durable_year || 0) <= 2
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: '改定取得価額は耐用年数が3年以上の場合入力できます。',
                path: ['revised_acquisition_amount']
              })
            }
            if (data.special_depreciation_amount > data.beginning_balance) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  '特別償却費が期首残高を超えています。特別償却額の金額を変更して下さい。',
                path: ['special_depreciation_amount']
              })
            }
            if (
              data.depreciation_method === VOLUNTARY &&
              data.special_depreciation_amount +
                (data.depreciation_amount || 0) >
                data.beginning_balance
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  '本年の償却額と特別償却費の合計が期首残高を超えています。本年の償却額または特別償却費を変更して下さい。',
                path: ['special_depreciation_amount']
              })
            }
          })
      ),
    [fixedAsset]
  )

  type FormInputs = z.infer<typeof schema>
  const [isShowDetail, { toggle: toggleIsShowDetail }] = useBoolean()

  const methods = useForm<FormInputs>({
    defaultValues: {
      depreciation_method: fixedAsset?.depreciation_method || STRAIGHT_LINE,
      depreciation_amount: fixedAsset?.depreciation?.depreciation_amount || 0,
      durable_year: fixedAsset?.durable_year || undefined,
      business_rate: fixedAsset?.depreciation?.business_rate || 100,
      service_date: fixedAsset?.service_date || fixedAsset?.acquisition_date,
      amount_rounding: fixedAsset?.amount_rounding || CEIL,
      beginning_balance: fixedAsset?.depreciation?.beginning_balance || 0,
      revised_acquisition_amount: fixedAsset?.revised_acquisition_amount || 0,
      special_depreciation_amount:
        fixedAsset?.depreciation?.special_depreciation_amount || 0
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema)
  })
  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting, errors }
  } = methods

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    await patchFixedAssetsDepreciationMethod(data)
    setIsEditing(-1)
    isOnCreateModal && onOpenCompleteDialog()
  }

  const resetForm = () =>
    reset({
      depreciation_method: fixedAsset?.depreciation_method || STRAIGHT_LINE,
      depreciation_amount: fixedAsset?.depreciation?.depreciation_amount || 0,
      durable_year: fixedAsset?.durable_year || undefined,
      business_rate: fixedAsset?.depreciation?.business_rate || 100,
      service_date: fixedAsset?.service_date || fixedAsset?.acquisition_date,
      amount_rounding: fixedAsset?.amount_rounding || CEIL,
      beginning_balance: fixedAsset?.depreciation?.beginning_balance || 0,
      revised_acquisition_amount: fixedAsset?.revised_acquisition_amount || 0,
      special_depreciation_amount:
        fixedAsset?.depreciation?.special_depreciation_amount || 0
    })
  useEffect(() => {
    resetForm()
  }, [fixedAsset])

  return (
    <FormProvider {...methods}>
      {renderCompleteDialog({
        header: '新規登録完了',
        body: '新しい固定資産の登録が完了しました！',
        okButton: {
          children: 'OK'
        }
      })}
      <FormSection
        index={3}
        title={title}
        id={id}
        editType={isOnCreateModal ? 'create' : 'edit'}
        // まだ前のフォームを保存していないときは編集できないようにする
        disabled={!done && !isEditing}
      >
        <Collapse in={!done || isEditing}>
          <Box>
            <Text fontSize="16px" lineHeight="20px" fontWeight={700}>
              1. 償却方法
            </Text>
            <FormControl isInvalid={!!errors.depreciation_method?.message}>
              <RadioGroup
                isDisabled={hasPreviousDepreciation}
                mt="16px"
                display="flex"
                flexDir="column"
                onChange={(
                  nextValue: request_fixedAssets_depreciationMethod.depreciation_method
                ) => {
                  setValue('depreciation_method', nextValue)
                  if (fixedAsset?.account_item.name === '開業費') {
                    if (nextValue === VOLUNTARY) {
                      setValue('durable_year', undefined)
                    }
                    if (nextValue === STRAIGHT_LINE_METHOD) {
                      setValue('durable_year', 5)
                    }
                  }
                  if (nextValue === PETTY_SUM) {
                    setValue('durable_year', 1)
                  }
                  if (nextValue === LUMP_SUM) {
                    setValue('durable_year', 3)
                  }
                }}
                value={watch('depreciation_method')}
              >
                {fixedAsset?.account_item.name !== '開業費' && (
                  <>
                    <Radio
                      alignItems="flex-start"
                      px="14px"
                      value={STRAIGHT_LINE}
                    >
                      <Box>
                        <Flex gap="4px">
                          <Text>定額法</Text>
                          <Box
                            color="white.100"
                            bgColor="primary.purple"
                            borderRadius="4px"
                            py="2px"
                            px="4px"
                            fontSize="10px"
                            lineHeight={1}
                            fontWeight={500}
                          >
                            原則
                          </Box>
                        </Flex>
                        <Text
                          mt="8px"
                          fontSize="13px"
                          lineHeight="20.8px"
                          fontWeight={500}
                          color="primary.purple"
                        >
                          特に事情がなれけばこちらを選択しましょう！
                        </Text>
                      </Box>
                    </Radio>
                    <Radio
                      alignItems="flex-start"
                      px="14px"
                      value={DECLINING_BALANCE}
                    >
                      <Box mt="-2px">
                        <Text lineHeight="24px">定率法</Text>
                        {watch('depreciation_method') ===
                          request_fixedAssets_depreciationMethod
                            .depreciation_method.DECLINING_BALANCE && (
                          <AlertMessage
                            mt="8px"
                            title="定率法を選ぶには、以下の書類を税務署に提出する必要があります"
                            content={
                              <Flex gap="2px" flexDir="column">
                                <Flex gap="4px" align="center">
                                  <Text fontSize="13px" lineHeight="20.8px">
                                    書類名：
                                  </Text>
                                  <Flex
                                    gap="6px"
                                    align="center"
                                    color="gray.900"
                                  >
                                    <Link
                                      fontWeight={700}
                                      fontSize="13px"
                                      lineHeight={1}
                                      textDecor="underline"
                                      href="https://www.nta.go.jp/taxes/tetsuzuki/shinsei/annai/shinkoku/pdf/h28/18_19.pdf"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      所得税の減価償却資産の償却方法の届出書
                                    </Link>
                                    <ExternalLinkIcon fontSize="20px" />
                                  </Flex>
                                </Flex>
                                <Flex gap="4px" align="center">
                                  <Text fontSize="13px" lineHeight="20.8px">
                                    期限日：
                                  </Text>
                                  <Text
                                    fontWeight={700}
                                    fontSize="13px"
                                    color="gray.900"
                                    lineHeight={1}
                                  >
                                    資産を購入した年度の確定申告期限まで
                                  </Text>
                                </Flex>
                              </Flex>
                            }
                          />
                        )}
                      </Box>
                    </Radio>
                    <Radio alignItems="flex-start" px="14px" value={PETTY_SUM}>
                      <Text lineHeight="24px" mt="-2px">
                        少額償却
                      </Text>
                      {watch('depreciation_method') === PETTY_SUM && (
                        <AlertMessage
                          mt="8px"
                          title="少額償却を選ぶには、あなたが青色申告者である必要があります"
                        />
                      )}
                    </Radio>
                    <Radio px="14px" value={LUMP_SUM}>
                      一括償却
                    </Radio>
                  </>
                )}
                {fixedAsset?.account_item.name === '開業費' && (
                  <>
                    <Radio px="14px" value={VOLUNTARY}>
                      <Text>任意償却</Text>
                    </Radio>
                    {watch('depreciation_method') === VOLUNTARY && (
                      <Box pl="16px">
                        <SubFormSection
                          title={`${
                            processing_year?.processing_year ||
                            new Date().getFullYear()
                          }年度の償却額はいくらにするか入力しましょう（自由に設定できます）`}
                        >
                          <FormPriceAmountInput<FormInputs>
                            w="180px"
                            name="depreciation_amount"
                            placeholder="0"
                          />
                        </SubFormSection>
                      </Box>
                    )}
                    <Radio px="14px" value={STRAIGHT_LINE_METHOD}>
                      <Text>均等償却</Text>
                    </Radio>
                  </>
                )}
              </RadioGroup>
              <FormErrorMessage>
                {errors.depreciation_method?.message}
              </FormErrorMessage>
            </FormControl>
          </Box>

          <Flex justify="space-between" my="40px">
            <VStack spacing="12px" align="flex-start">
              <Text
                fontSize="16px"
                lineHeight="20px"
                fontWeight={700}
                position="relative"
              >
                2. 耐用年数
                <QuestionTooltip
                  pos="absolute"
                  right="-24px"
                  top="2px"
                  label={
                    <>
                      国が定めた固定資産を使える期間のことです。右の表や国税庁の
                      <Link
                        textDecor="underline"
                        href="https://www.keisan.nta.go.jp/h29yokuaru/aoiroshinkoku/hitsuyokeihi/genkashokyakuhi/taiyonensuhyo.html"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        耐用年数表
                      </Link>
                      からご確認ください。
                    </>
                  }
                />
              </Text>
              <Text
                mb="12px"
                fontWeight={500}
                fontSize="14px"
                lineHeight="22.4px"
              >
                この資産の「耐用年数」を調べて、
                <br />
                年数を入力しましょう
              </Text>
              <Flex gap="8px" align="flex-end">
                <FormNumberInput<FormInputs>
                  w="64px"
                  placeholder="例）4"
                  name="durable_year"
                  isDisabled={
                    hasPreviousDepreciation ||
                    watch('depreciation_method') === LUMP_SUM ||
                    watch('depreciation_method') === PETTY_SUM ||
                    watch('depreciation_method') === VOLUNTARY ||
                    watch('depreciation_method') === STRAIGHT_LINE_METHOD
                  }
                />
                <Text fontWeight={500} fontSize="15px" lineHeight="22.5px">
                  年
                </Text>
              </Flex>
            </VStack>
            <Box>
              <Text
                mb="10px"
                fontSize="13px"
                lineHeight={1}
                fontWeight={700}
                color="gray.800"
              >
                ■ 主な耐用年数表
              </Text>
              {[
                { name: 'パソコン', year: 4 },
                { name: 'スマホ', year: 10 },
                { name: 'カメラ', year: 5 },
                { name: '椅子(金属)', year: 15 },
                { name: '椅子(非金属)', year: 8 }
              ].map((content) => (
                <Flex
                  key={content.name}
                  border="1px solid"
                  borderColor="gray.200"
                  sx={{
                    'div + &': { borderTop: '0px' }
                  }}
                >
                  <Box
                    fontSize="13px"
                    lineHeight={1}
                    fontWeight={400}
                    bgColor="gray.200"
                    py="9.5px"
                    pl="16px"
                    w="120px"
                  >
                    {content.name}
                  </Box>
                  <Box
                    className="year"
                    fontSize="14px"
                    lineHeight={1}
                    fontWeight={400}
                    py="9px"
                    pl="16px"
                    w="120px"
                  >
                    {content.year}年
                  </Box>
                </Flex>
              ))}
              <Flex mt="10px" h="20px" align="center" color="gray.900">
                <Link
                  fontWeight={700}
                  fontSize="13px"
                  lineHeight={1}
                  textDecor="underline"
                  href="https://www.keisan.nta.go.jp/h29yokuaru/aoiroshinkoku/hitsuyokeihi/genkashokyakuhi/taiyonensuhyo.html "
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  上表に無い場合はこちらで調べましょう（国税庁）
                </Link>
                <ExternalLinkIcon fontSize="20px" />
              </Flex>
            </Box>
          </Flex>
          <Collapse in={isShowDetail}>
            <Text fontSize="16px" lineHeight="20px" fontWeight={700} mb="16px">
              3. もっと細かな設定
            </Text>
            <VStack spacing="16px" align="flex-start">
              <FormYearSelect<FormInputs>
                w="180px"
                label="使用開始日"
                name="service_date"
                helpTooltipText="個人のお仕事のために固定資産を使い始めた日を入力しましょう。"
                isDisabled={hasPreviousDepreciation}
                yearProps={{
                  start: fixedAsset?.acquisition_date
                    ? new Date(fixedAsset?.acquisition_date).getFullYear()
                    : new Date().getFullYear() - 3,
                  end: processing_year?.processing_year
                }}
                max={`${
                  processing_year?.processing_year || new Date().getFullYear()
                }-12-31`}
              />
              <Box pos="relative">
                <FormNumberInput<FormInputs>
                  w="180px"
                  label="事業利用割合"
                  name="business_rate"
                  helpTooltipText="個人の仕事のみに使用する場合は「100」と入力しましょう。プライベートでも利用する場合は、仕事における利用の割合を入力しましょう。割合の決め方に決まりはありませんが、利用時間・日数を元に決めることが多いです"
                  min={0}
                  max={100}
                />
                <Text pos="absolute" top="37px" right="12px" zIndex={1}>
                  ％
                </Text>
              </Box>

              <FormSelect<
                FormInputs,
                request_fixedAssets_depreciationMethod.amount_rounding
              >
                w="180px"
                label="端数処理"
                name="amount_rounding"
                placeholder="未選択"
                isDisabled={hasPreviousDepreciation}
              >
                <option value={CEIL}>切り上げ</option>
                <option value={FLOOR}>切り下げ</option>
                <option value={ROUND}>四捨五入</option>
              </FormSelect>
              <FormPriceAmountInput<FormInputs>
                isDisabled={
                  hasPreviousDepreciation ||
                  isAcquisitionDateIsSameAsProcessingYear
                }
                w="180px"
                label="期首残高"
                name="beginning_balance"
              />
              <FormPriceAmountInput<FormInputs>
                isDisabled={
                  hasPreviousDepreciation ||
                  isAcquisitionDateIsSameAsProcessingYear ||
                  (watch('durable_year') || 0) <= 2
                }
                w="180px"
                label="改定取得価額"
                helpTooltipText="通常は空欄でOKです"
                name="revised_acquisition_amount"
              />
              <FormPriceAmountInput<FormInputs>
                w="180px"
                label="特別（割増）償却費"
                name="special_depreciation_amount"
                helpTooltipText="通常は空欄でOKです"
              />
            </VStack>
          </Collapse>
          <Flex mt="16px" align="center">
            <TriangleArrowRightIcon
              transform={`rotate(${isShowDetail ? 270 : 90}deg)`}
              transition="transform ease .5s"
              w="20px"
              h="20px"
              fill="gray.600"
            />
            <Text
              display="inline-block"
              color="purple.900"
              textDecoration="underline"
              fontSize="13px"
              lineHeight={1}
              fontWeight={700}
              cursor="pointer"
              onClick={toggleIsShowDetail}
            >
              {isShowDetail ? 'もっと細かな設定を閉じる' : 'もっと細かな設定'}
            </Text>
          </Flex>
          <SaveButtons
            mt="32px"
            isSubmitting={isSubmitting}
            onSave={handleSubmit(onSubmit)}
            onCancel={
              done
                ? () => {
                    setIsEditing(-1)
                    resetForm()
                  }
                : undefined
            }
            saveButtonLabel={
              isOnCreateModal ? '保存して固定資産を登録' : '保存'
            }
          />
        </Collapse>

        {done &&
          !isEditing &&
          (isOnCreateModal ? (
            <Box>
              <Text
                fontSize="20px"
                fontWeight={700}
                mb="18px"
              >{`${getDepreciationMethodName(
                fixedAsset?.depreciation_method
              )} ${
                fixedAsset?.durable_year ? `${fixedAsset?.durable_year}年` : ''
              }`}</Text>
              <Button variant="outline" onClick={() => setIsEditing(index)}>
                編集する
              </Button>
            </Box>
          ) : (
            <Box>
              <Flex flexDir="column" gap="24px">
                {fixedAsset &&
                  [
                    {
                      label: '償却方法',
                      name: getDepreciationMethodName(
                        fixedAsset?.depreciation_method
                      )
                    },
                    {
                      label: '耐用年数',
                      name: fixedAsset?.durable_year
                        ? `${fixedAsset?.durable_year}年`
                        : '-'
                    },
                    {
                      label: '使用開始日',
                      name: format(
                        new Date(fixedAsset.service_date),
                        'yyyy/MM/dd'
                      )
                    },
                    {
                      label: '事業利用割合',
                      name: fixedAsset?.depreciation?.business_rate
                        ? `${fixedAsset?.depreciation?.business_rate}%`
                        : '-'
                    },
                    {
                      label: '端数処理',
                      name: getAmountRoundingMethodName(
                        fixedAsset.amount_rounding
                      )
                    },
                    {
                      label: '特別（割増）償却費',
                      name: fixedAsset.depreciation?.special_depreciation_amount
                        ? `${fixedAsset.depreciation?.special_depreciation_amount}円`
                        : '-'
                    },
                    {
                      label: '期首残高',
                      name: `${
                        fixedAsset.depreciation?.beginning_balance || 0
                      }円`
                    },
                    {
                      label: '改定取得価額',
                      name: fixedAsset.revised_acquisition_amount
                        ? `${fixedAsset.revised_acquisition_amount}円`
                        : '-'
                    }
                  ].map((label, i) => <DataDisplay key={i} {...label} />)}
              </Flex>
              <Button
                mt="28px"
                variant="outline"
                onClick={() => setIsEditing(index)}
              >
                編集する
              </Button>
            </Box>
          ))}
      </FormSection>
    </FormProvider>
  )
}
